import { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/config/axiosConfig';
import SlidePanel from '@/theme/partials/slidepanel';
import { isValidGmail, getUserIdFromEmail } from '@/utils/config/userHelpers';

export const useConfig = (t, language, user, userInfoLoaded, changeLanguage) => {
    const [saveStatus, setSaveStatus] = useState({ show: false, success: false, message: "" });
    
    // Toast notification state
    const [toast, setToast] = useState({
        open: false,
        severity: "success",
        message: ""
    });
    
    // User management state
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [newUser, setNewUser] = useState({
        email: "",
        role_id: ""
    });
    const [emailError, setEmailError] = useState("");
    const [loading, setLoading] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // Slideout panel state
    const { isOpen, openPanel, closePanel } = SlidePanel.useSlidePanel();
    const [currentUser, setCurrentUser] = useState(null);
    const slidePanel = { isOpen, openPanel, closePanel };

    // Delete confirmation dialog state
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        userId: null,
        email: ""
    });

    // Load data when user info is loaded
    useEffect(() => {
        if (userInfoLoaded) {
            checkAccessAndLoadData();
        }
    }, [userInfoLoaded]);

    // Check access permissions and load data if authorized
    const checkAccessAndLoadData = async () => {
        try {
            setInitialLoading(true);
            
            // Check if user has root access using user info from useAuth
            const userRole = user?.role;
            const isRoot = userRole === 'root';
            setHasAccess(isRoot);
            
            if (isRoot) {
                // Load roles and users if user is root
                const rolesResponse = await axios.get('/roles');
                setRoles(rolesResponse.data.data || []);
                
                // Now fetch users data
                await fetchUsers();
            }
        } catch (error) {
            // If there's an error, likely a permissions issue
            setHasAccess(false);
            showToast("error", error.response?.data?.message || t('user.noPermission'));
        } finally {
            setInitialLoading(false);
        }
    };

    // Fetch users from API
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/whitelist');
            setUsers(response.data.data || []);
        } catch (error) {
            showToast("error", error.response?.data?.message || t('user.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    // Handle language change
    const handleLanguageChange = (event) => {
        const newLanguage = event.target.value;
        // Kiểm tra đảm bảo changeLanguage tồn tại trước khi gọi
        if (typeof changeLanguage === 'function') {
            changeLanguage(newLanguage);
            // Show success toast immediately after language change
            showToast("success", t('config.saveSuccess'));
        } else {
            console.error("changeLanguage function is not defined");
            // Fallback nếu không có hàm changeLanguage
            showToast("error", t('config.saveError'));
        }
    };

    // Display toast notification
    const showToast = (severity, message) => {
        setToast({
            open: true,
            severity,
            message
        });
    };

    // Close toast notification
    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setToast({ ...toast, open: false });
    };

    // Handle input changes for new user
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'email') {
            setEmailError(""); // Clear error when user types
        }
        
        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Add new user to whitelist
    const addUser = async () => {
        if (!newUser.email) {
            showToast("error", t('user.emailRequired'));
            return;
        }

        // Validate Gmail address
        if (!isValidGmail(newUser.email)) {
            setEmailError(t('user.invalidGmail'));
            return;
        }

        if (!newUser.role_id) {
            showToast("error", t('user.roleRequired'));
            return;
        }

        try {
            setLoading(true);
            await axios.post('/whitelist', {
                email: newUser.email,
                role_id: newUser.role_id
            });
            
            // Fetch updated list or add the new user to the state
            await fetchUsers();
            
            // Reset form
            setNewUser({ email: "", role_id: "" });
            setEmailError("");
            
            showToast("success", t('user.addSuccess'));
        } catch (error) {
            showToast("error", error.response?.data?.message || t('user.addError'));
        } finally {
            setLoading(false);
        }
    };

    // Open slideout panel
    const openSlideout = (user) => {
        setCurrentUser(user);
        openPanel();
    };

    // Close slideout panel
    const closeSlideout = () => {
        setCurrentUser(null);
        closePanel();
    };

    // Handle role change in slideout
    const handleRoleChange = (e) => {
        const newRoleId = e.target.value;
        setCurrentUser(prev => ({
            ...prev,
            role_id: newRoleId
        }));
    };

    // Save changes from slideout
    const saveUserChanges = async () => {
        if (!currentUser) return;
        
        try {
            setLoading(true);
            // Use email prefix as userId
            const userId = getUserIdFromEmail(currentUser.email);
            await axios.put(`/whitelist/${userId}`, {
                role_id: currentUser.role_id,
                is_active: currentUser.is_active
            });
            
            // Update local state
            setUsers(prevUsers => prevUsers.map(user => 
                user.email === currentUser.email ? { ...user, ...currentUser } : user
            ));
            
            showToast("success", t('user.updateSuccess'));
            closeSlideout();
        } catch (error) {
            showToast("error", error.response?.data?.message || t('user.updateError'));
        } finally {
            setLoading(false);
        }
    };

    // Toggle user active status in slideout and call API
    const toggleUserStatus = async () => {
        if (!currentUser) return;
        
        // Toggle the status
        const newStatus = currentUser.is_active === 1 ? 0 : 1;
        
        try {
            setLoading(true);
            // Get userId from email
            const userId = getUserIdFromEmail(currentUser.email);
            
            // Call API to update status
            await axios.put(`/whitelist/${userId}`, {
                role_id: currentUser.role_id,
                is_active: newStatus
            });
            
            // Update local state for the current user in slideout
            setCurrentUser(prev => ({
                ...prev,
                is_active: newStatus
            }));
            
            // Update users list state
            setUsers(prevUsers => prevUsers.map(user => 
                user.email === currentUser.email ? { ...user, is_active: newStatus } : user
            ));
            
            // Show success toast
            showToast("success", newStatus === 1 
                ? (language === 'vi' ? 'Kích hoạt người dùng thành công' : 'User activated successfully') 
                : (language === 'vi' ? 'Vô hiệu hóa người dùng thành công' : 'User deactivated successfully'));
        } catch (error) {
            showToast("error", error.response?.data?.message || t('user.updateError'));
        } finally {
            setLoading(false);
        }
    };

    // Open delete confirmation dialog
    const openDeleteDialog = (email) => {
        setDeleteDialog({
            open: true,
            userId: getUserIdFromEmail(email),
            email: email
        });
        
        // If slideout is open, close it
        if (isOpen) {
            closeSlideout();
        }
    };

    // Close delete confirmation dialog
    const closeDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            userId: null,
            email: ""
        });
    };

    // Remove user from whitelist
    const removeUser = async (email) => {
        if (!email) return;
        
        try {
            setLoading(true);
            // Extract userId from email (everything before @)
            const userId = getUserIdFromEmail(email);
            
            await axios.delete(`/whitelist/${userId}`);
            
            // Update local state
            setUsers(prevUsers => prevUsers.filter(user => user.email !== email));
            
            showToast("success", t('user.removeSuccess'));
        } catch (error) {
            showToast("error", error.response?.data?.message || t('user.removeError'));
        } finally {
            setLoading(false);
        }
    };

    // Confirm user deletion
    const confirmDelete = async () => {
        await removeUser(deleteDialog.email);
        closeDeleteDialog();
    };

    return {
        hasAccess,
        initialLoading,
        users,
        roles,
        newUser,
        emailError,
        loading,
        toast,
        slidePanel,
        deleteDialog,
        saveStatus,
        currentUser,
        fetchUsers,
        handleLanguageChange,
        handleInputChange,
        addUser,
        openSlideout,
        closeSlideout,
        handleRoleChange,
        saveUserChanges,
        toggleUserStatus,
        openDeleteDialog,
        closeDeleteDialog,
        confirmDelete,
        handleCloseToast,
        showToast
    };
};